<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import UIKit from '@pocketprep/ui-kit'

const { Checkbox } = UIKit

type QuestionDifficultyCounts = {
    easy: number
    medium: number
    hard: number
}

const props = withDefaults(defineProps<{
    questionDifficultyCounts: QuestionDifficultyCounts
    modelValue?: (keyof QuestionDifficultyCounts)[]
    disabled?: boolean
}>(), {
    modelValue: () => [],
    disabled: false,
})

const emit = defineEmits<{
    'update:modelValue': [value: (keyof QuestionDifficultyCounts)[]]
}>()

const difficultyOptions: {
    name: (keyof QuestionDifficultyCounts)
    label: string
}[] = [
    {
        name: 'easy',
        label: 'Easy',
    },
    {
        name: 'medium',
        label: 'Medium',
    },
    {
        name: 'hard',
        label: 'Hard',
    },
]
const difficultiesSelected = ref<(keyof QuestionDifficultyCounts)[]>([])

const difficultyClicked = (difficultyName: keyof QuestionDifficultyCounts, isSelected: boolean) => {
    if (!isSelected) {
        difficultiesSelected.value = difficultiesSelected.value.filter(d => d !== difficultyName)
    } else {
        difficultiesSelected.value.push(difficultyName)
    }
}

const emitDifficultiesChanged = () => {
    emit('update:modelValue', difficultiesSelected.value)
    return difficultiesSelected.value
}

onMounted(() => {
    difficultiesSelected.value = props.modelValue
})

watch(() => props.modelValue, () => {
    difficultiesSelected.value = props.modelValue
}, { deep: true })

watch(difficultiesSelected, () => {
    emitDifficultiesChanged()
}, { deep: true })
</script>

<template>
    <div class="difficulty-selector">
        <div class="difficulty-selector__label">Include Questions with Difficulty:</div>
        <div class="difficulty-selector__option"
             v-for="difficultyOption in difficultyOptions"
             :key="difficultyOption.name"
        >
            <div :class="{
                'difficulty-selector__option-label': true,
                [`difficulty-selector__option-label--${difficultyOption.name}`]: true
            }">
                {{ difficultyOption.label }}
            </div>
            <div class="difficulty-selector__option-right">
                <div class="difficulty-selector__option-count">
                    {{ questionDifficultyCounts[difficultyOption.name] }}
                </div>
                <div class="difficulty-selector__option-checkbox">
                    <Checkbox
                        :modelValue="difficultiesSelected.includes(difficultyOption.name)"
                        :disabled="disabled"
                        @update:modelValue="(isChecked: boolean) => difficultyClicked(difficultyOption.name, isChecked)"
                        :aria-labelledby="`difficulty-selector__option-label--${difficultyOption.name}`"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.difficulty-selector {
    border: 1px solid $gray-divider;
    background: $white;
    border-radius: 6px;
    font-size: 15px;
    line-height: 20px;
    font-weight: 500;
    padding: 16px 12px 14px 15px;

    &__label {
        margin-bottom: 12px;
        cursor: default;
    }

    &__option {
        margin-bottom: 18px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: default;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__option-right {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    &__option-count {
        color: $slate;
        font-size: 14px;
        margin-right: 14px;
    }
}
</style>
